import React, { useState, useEffect } from 'react';

interface ResonateIconProps {
  /** Whether the icon is currently in a resonated (active) state */
  isResonated: boolean;
  /** Whether this is for nested comments (affects color scheme) */
  isNested?: boolean;
  /** Size of the icon in pixels */
  size?: number;
  /** Type of resonance icon to display */
  iconType?: 'soundwave' | 'intersection';
  /** Additional CSS classes */
  className?: string;
  /** Show activation pop animation */
  showActivationPop?: boolean;
}

const ResonateIcon: React.FC<ResonateIconProps> = ({
  isResonated,
  isNested = false,
  size = 18,
  // iconType = 'soundwave',
  className = '',
  showActivationPop = false
}) => {
  const [animationStage, setAnimationStage] = useState<'idle' | 'starting' | 'merging' | 'pulsing' | 'complete'>('idle');
  const [showPop, setShowPop] = useState(false);

  useEffect(() => {
    if (isResonated && animationStage === 'idle') {
      // First just mark as starting (circles don't move yet)
      setAnimationStage('starting');
      if (showActivationPop) {
        setShowPop(true);
        setTimeout(() => setShowPop(false), 400);
      }
      
      // Small delay before starting the merge
      setTimeout(() => {
        setAnimationStage('merging');
      }, 100);
      
      // After merge completes, start pulsing
      setTimeout(() => {
        setAnimationStage('pulsing');
      }, 4600);
      
      // After pulsing completes, show final state
      setTimeout(() => {
        setAnimationStage('complete');
      }, 7100);
    } else if (!isResonated) {
      setAnimationStage('idle');
    }
  }, [isResonated, animationStage, showActivationPop]);

  const colors = {
    circle1: isNested ? '#B08FCC' : '#FC5373',
    circle2: isNested ? '#CC8FB0' : '#FC8853',
    merged: isNested ? '#B08FCC' : '#FC53C7'
  };

  // const isStarting = animationStage === 'starting';
  const isMerging = animationStage === 'merging';
  const isPulsing = animationStage === 'pulsing';
  const isComplete = animationStage === 'complete';
  const showMerged = isPulsing || isComplete;
  const shouldMove = isMerging || showMerged;

  return (
    <div 
      className={`relative inline-flex items-center justify-center align-middle ${className}`}
      style={{ width: size, height: size }}
    >
      <style>
        {`
          @keyframes pulse-organic-slow {
            0% {
              transform: scale(1);
              opacity: 0.8;
            }
            20% {
              transform: scale(1.03);
              opacity: 0.85;
            }
            40% {
              transform: scale(1);
              opacity: 0.82;
            }
            60% {
              transform: scale(1.02);
              opacity: 0.87;
            }
            80% {
              transform: scale(1.01);
              opacity: 0.84;
            }
            100% {
              transform: scale(1);
              opacity: 0.8;
            }
          }

          @keyframes glow-pulse-subtle {
            0% {
              opacity: 0.15;
              transform: scale(1);
            }
            20% {
              opacity: 0.25;
              transform: scale(1.05);
            }
            40% {
              opacity: 0.18;
              transform: scale(1.02);
            }
            60% {
              opacity: 0.22;
              transform: scale(1.04);
            }
            80% {
              opacity: 0.17;
              transform: scale(1.01);
            }
            100% {
              opacity: 0.15;
              transform: scale(1);
            }
          }

          .animate-pulse-organic-slow {
            animation: pulse-organic-slow 2.5s ease-in-out;
          }

          .animate-glow-pulse-subtle {
            animation: glow-pulse-subtle 2.5s ease-in-out;
          }
        `}
      </style>
      
      <svg
        width={size}
        height={size}
        viewBox="0 0 24 24"
        fill="none"
        className={`transition-transform duration-300 ${showPop ? 'scale-110' : 'scale-100'}`}
      >
        <defs>
          <radialGradient id="glow-gradient-subtle">
            <stop offset="0%" stopColor={colors.merged} stopOpacity="0.08" />
            <stop offset="50%" stopColor={colors.merged} stopOpacity="0.04" />
            <stop offset="100%" stopColor={colors.merged} stopOpacity="0" />
          </radialGradient>
          
          <filter id="glow-filter-subtle">
            <feGaussianBlur stdDeviation="0.5" result="coloredBlur"/>
            <feMerge>
              <feMergeNode in="coloredBlur"/>
              <feMergeNode in="SourceGraphic"/>
            </feMerge>
          </filter>
        </defs>

        {/* Circle 1 - Left/Top */}
        <circle
          cx={shouldMove ? 12 : 9}
          cy={shouldMove ? 12 : 9}
          r={7}
          fill="none"
          stroke={isResonated ? colors.circle1 : 'currentColor'}
          strokeWidth="1"
          className={`transition-all ${isMerging ? 'duration-[4500ms]' : 'duration-500'} ease-in-out`}
          style={{
            opacity: showMerged ? 0 : 1,
          }}
        />

        {/* Circle 2 - Right/Bottom */}
        <circle
          cx={shouldMove ? 12 : 15}
          cy={shouldMove ? 12 : 15}
          r={7}
          fill="none"
          stroke={isResonated ? colors.circle2 : 'currentColor'}
          strokeWidth="1"
          className={`transition-all ${isMerging ? 'duration-[4500ms]' : 'duration-500'} ease-in-out`}
          style={{
            opacity: showMerged ? 0 : 1,
          }}
        />

        {/* Merged Circle - Only visible after merging */}
        {showMerged && (
          <g filter="url(#glow-filter-subtle)">
            <circle
              cx="12"
              cy="12"
              r="9.5"
              fill="none"
              stroke={colors.merged}
              strokeWidth="1"
              className={`${isPulsing ? 'animate-pulse-organic-slow' : ''}`}
              style={{
                opacity: isPulsing ? 0.8 : 0.9,
              }}
            />
            {/* Very subtle glow effect */}
            <circle
              cx="12"
              cy="12"
              r="11.5"
              fill="url(#glow-gradient-subtle)"
              className={`${isPulsing ? 'animate-glow-pulse-subtle' : ''}`}
              style={{
                opacity: isPulsing ? 1 : 0.5,
              }}
            />
          </g>
        )}
      </svg>
    </div>
  );
};

export default ResonateIcon;